import { useState } from 'react';
import { useTheme } from './theme';

/**
 * AML Model Insights - Display quantitative model predictions
 * Shows risk components, confidence, and provides feedback mechanism
 */
export function AMLModelInsights({ transaction, score, onFeedback }) {
  const C = useTheme();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isTruePositive, setIsTruePositive] = useState(null);

  if (!score) return null;

  const {
    riskScore = 0,
    components = {},
    explanation = '',
    confidence = 0,
    shouldAlert = false,
    isFlagged = false,
  } = score;

  const getRiskColor = (score) => {
    if (score >= 70) return '#FF6B6B'; // Red - Alert
    if (score >= 50) return '#FFA500'; // Orange - Flag
    if (score >= 30) return '#FFD700'; // Yellow - Caution
    return '#4CAF50'; // Green - Low
  };

  const getRiskLabel = (score) => {
    if (score >= 70) return 'ALERT';
    if (score >= 50) return 'FLAGGED';
    if (score >= 30) return 'CAUTION';
    return 'LOW RISK';
  };

  const handleSubmitFeedback = async () => {
    if (isTruePositive === null) {
      alert('Please select True or False Positive');
      return;
    }

    await onFeedback({
      transactionId: transaction.id,
      isTruePositive,
      notes: feedback,
      originalScore: riskScore,
    });

    setFeedback('');
    setIsTruePositive(null);
    setShowFeedback(false);
  };

  return (
    <div
      style={{
        background: C.surface,
        border: `2px solid ${getRiskColor(riskScore)}`,
        borderRadius: 12,
        padding: 20,
        marginTop: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: getRiskColor(riskScore),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 32,
              fontWeight: 'bold',
              fontFamily: "'DM Mono',monospace",
            }}
          >
            {riskScore}
          </div>
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: getRiskColor(riskScore),
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {getRiskLabel(riskScore)}
            </div>
            <div style={{ fontSize: 12, color: C.textDim, marginTop: 4 }}>
              Confidence: {confidence}%
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
              Quantitative AML Model v1.0
            </div>
          </div>
        </div>

        {shouldAlert && (
          <div
            style={{
              background: '#FF6B6B',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            ⚠ ALERT
          </div>
        )}
        {isFlagged && !shouldAlert && (
          <div
            style={{
              background: '#FFA500',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            🚩 FLAGGED
          </div>
        )}
      </div>

      {/* Explanation */}
      <div
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
          fontSize: 13,
          fontStyle: 'italic',
          color: C.text,
        }}
      >
        {explanation}
      </div>

      {/* Risk Components */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.textDim,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 12,
          }}
        >
          Risk Components
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {Object.entries(components)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => (
              <div
                key={key}
                style={{
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  padding: 10,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>
                  {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color:
                      value > 70
                        ? '#FF6B6B'
                        : value > 40
                          ? '#FFA500'
                          : value > 0
                            ? '#FFD700'
                            : C.textDim,
                  }}
                >
                  {Math.round(value)}
                </div>
                <div
                  style={{
                    width: '100%',
                    height: 3,
                    background: C.border,
                    borderRadius: 2,
                    marginTop: 6,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${value}%`,
                      height: '100%',
                      background: getRiskColor(value),
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Model Learning Feedback */}
      {!showFeedback ? (
        <button
          onClick={() => setShowFeedback(true)}
          style={{
            background: C.primary,
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          📊 Provide Feedback to Improve Model
        </button>
      ) : (
        <div
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: 16,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: C.text,
              marginBottom: 12,
            }}
          >
            Help improve the model: Is this a real risk?
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => setIsTruePositive(true)}
              style={{
                flex: 1,
                background: isTruePositive === true ? '#4CAF50' : C.bg,
                color: isTruePositive === true ? 'white' : C.text,
                border: `2px solid ${isTruePositive === true ? '#4CAF50' : C.border}`,
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              ✓ True Positive
            </button>
            <button
              onClick={() => setIsTruePositive(false)}
              style={{
                flex: 1,
                background: isTruePositive === false ? '#FFA500' : C.bg,
                color: isTruePositive === false ? 'white' : C.text,
                border: `2px solid ${isTruePositive === false ? '#FFA500' : C.border}`,
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              ✗ False Positive
            </button>
          </div>

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="(Optional) Why? What signals did we miss or overweight?"
            style={{
              width: '100%',
              minHeight: 60,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              padding: 10,
              fontSize: 12,
              color: C.text,
              fontFamily: "'DM Mono',monospace",
              marginBottom: 12,
              resize: 'vertical',
            }}
          />

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleSubmitFeedback}
              style={{
                flex: 1,
                background: C.primary,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Submit Feedback
            </button>
            <button
              onClick={() => {
                setShowFeedback(false);
                setFeedback('');
                setIsTruePositive(null);
              }}
              style={{
                flex: 1,
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Model Info */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${C.border}`,
          fontSize: 11,
          color: C.textDim,
        }}
      >
        <strong>ℹ How it works:</strong> The quantitative model learns from analyst
        feedback. Each time you mark an alert as true or false positive, the model
        adjusts its risk signals to improve future predictions. Confidence reflects
        agreement between multiple risk indicators.
      </div>
    </div>
  );
}

export default AMLModelInsights;
